import { LockOutlined, MailOutlined, PhoneOutlined, UserOutlined } from '@ant-design/icons';
import { Flex } from '@revenuemarket/cds';
import { colorTokens, thematicColors } from '@revenuemarket/design';
import { FormattedInput } from '@revenuemarket/ui/FormattedInput';
import { Form, Input, Modal, ModalProps, Typography } from 'antd';
import { Rule } from 'antd/es/form';
import Image from 'next/image';
import * as yup from 'yup';

import { COMMON_YUP } from '~constants/yup-scheme';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { mixpanel } from '~pages/_app';

interface Props extends ModalProps {
  onFinish?: (values: {
    memberName: string;
    memberId: string;
    memberPw: string;
    phoneNumber: string;
    email: string;
  }) => void;
}

const SignupInfoStepScheme = yup.object({
  id: yup
    .string()
    .required('아이디를 입력해주세요.')
    .min(6, '6자 이상의 아이디를 입력해주세요')
    .max(20, '20자 이하의 아이디를 입력해주세요'),
  email: COMMON_YUP.email,
  password: yup
    .string()
    .required('비밀번호를 입력해주세요.')
    .min(6, '6자 이상의 비밀번호를 입력해주세요')
    .max(20, '20자 이하의 비밀번호를 입력해주세요'),
  passwordConfirm: yup
    .string()
    .required('비밀번호를 입력해주세요.')
    .min(6, '6자 이상의 비밀번호를 입력해주세요')
    .max(20, '20자 이하의 비밀번호를 입력해주세요')
    .oneOf([yup.ref('password')], '비밀번호와 일치하지 않습니다.'),
  phoneNumber: COMMON_YUP.phoneNumber,
  name: yup
    .string()
    .required('이름을 2자 이상 입력해주세요.')
    .min(2, '이름을 2자 이상 입력해주세요.'),
});

const getSchemeRule = (field: string): Rule => ({
  validator: async (_, value) => {
    await SignupInfoStepScheme.validateAt(field, { [field]: value });
  },
});

const RegisterBaroBillModal: React.FC<Props> = ({ ...props }) => {
  const [form] = Form.useForm();
  const onRegister = (values: any) => {
    props.onFinish?.({
      memberName: values.name,
      memberId: values.id,
      memberPw: values.password,
      phoneNumber: values.phoneNumber,
      email: values.email,
    });
  };
  return (
    <Modal
      open={props.open}
      onCancel={props.onCancel}
      closable
      okText="가입하기"
      cancelText="취소"
      onOk={() => {
        mixpanel.track(MIXPANEL_EVENT.TAX_INVOICE.SIGNUP_BAROBILL);
        form.submit();
      }}
    >
      <Flex
        gap={24}
        style={{
          padding: '8px 24px 12px 24px',
        }}
        justify="center"
        align="center"
        direction="column"
      >
        <Flex align="center">
          <Image
            alt="barobill-icon"
            src="https://static.clobe.ai/image/clobe-logo-dark-icon.png"
            width={24}
            height={24}
            style={{
              height: 24,
            }}
          />
          <span style={{ color: colorTokens.textDescription }}>..........</span>
          <Image
            alt="barobill-icon"
            src="https://static.clobe.ai/image/barobill-icon.png"
            width={28}
            height={28}
            style={{
              height: 28,
            }}
          />
        </Flex>
        <Flex direction="column" gap={4} align="center">
          <Typography.Title level={5}>바로빌에 가입할 관리자 계정을 만듭니다</Typography.Title>
          <Typography.Text
            style={{
              color: thematicColors.primary.medium,
            }}
          >
            가입 후 관리자 계정으로 바로빌 홈페이지에 로그인할 수 있습니다.
          </Typography.Text>
        </Flex>
        <Form
          form={form}
          layout="vertical"
          onFinish={onRegister}
          style={{
            width: '100%',
          }}
        >
          <Form.Item name="id" label="아이디" required rules={[getSchemeRule('id')]}>
            <Input placeholder="아이디를 입력해주세요." />
          </Form.Item>
          <Form.Item name="password" label="비밀번호" required rules={[getSchemeRule('password')]}>
            <Input.Password
              placeholder="비밀번호를 입력해주세요."
              prefix={<LockOutlined style={{ color: colorTokens.iconDefault }} />}
            />
          </Form.Item>
          <Form.Item
            dependencies={['password']}
            style={{
              marginBottom: 0,
            }}
          >
            {({ getFieldsValue }) => (
              <Form.Item
                name="passwordConfirm"
                label="비밀번호 확인"
                required
                dependencies={['password']}
                rules={[
                  {
                    validator: async () => {
                      await SignupInfoStepScheme.validateAt('passwordConfirm', getFieldsValue());
                    },
                  },
                ]}
              >
                <Input.Password
                  placeholder="비밀번호 확인"
                  type="password"
                  prefix={<LockOutlined style={{ color: colorTokens.iconDefault }} />}
                />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item name="name" label="이름" required rules={[getSchemeRule('name')]}>
            <FormattedInput
              type="name"
              placeholder="이름을 입력해주세요"
              prefix={<UserOutlined style={{ color: colorTokens.iconDefault }} />}
              deformatter={(value) => value.replace(/[^ㄱ-ㅎ가-힣a-z]/g, '')}
            />
          </Form.Item>
          <Form.Item name="email" label="이메일" required rules={[getSchemeRule('email')]}>
            <Input
              type="email"
              placeholder="이메일을 입력해주세요"
              prefix={<MailOutlined style={{ color: colorTokens.iconDefault }} />}
            />
          </Form.Item>
          <Form.Item
            name="phoneNumber"
            label="휴대폰번호"
            required
            rules={[getSchemeRule('phoneNumber')]}
          >
            <FormattedInput
              type="phoneNumber"
              placeholder="휴대폰번호를 입력해주세요"
              prefix={<PhoneOutlined style={{ color: colorTokens.iconDefault }} />}
            />
          </Form.Item>
        </Form>
      </Flex>
    </Modal>
  );
};

export default RegisterBaroBillModal;
